import {FileLoader} from '@gongxigames/framework/core/services/resourceLoader/FileLoader';
import {
    Platform,
    ImageFormat,
    AudioFormat,
    AudioQuality,
    MetaPackageInfo,
    ResourcesMetaFile,
    PLATFORM_TYPE_MAP,
    BinaryDataImage,
    BinaryDataPackage,
} from '@gongxigames/framework/resourcesBuilder/types';
import {Sound} from '@pixi/sound';
import {decodeImageWebP, decodeImage} from '@gongxigames/framework/core/services/resourceLoader/SpriteParserGem';
import * as base64 from '@protobufjs/base64';
import {LoaderResource, Loader, Texture, BitmapFont, autoDetectFormat} from 'pixi.js';
import {TextureAtlas, ISkeletonData, SpineParser} from 'pixi-spine';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';

import {Resources, ResourcesLoaded} from '@gongxigames/framework/core/types';
import {errorReporter} from '@gongxigames/framework/core/utils';
import {coreStore} from '@gongxigames/framework/core/globals';
import msgpack from 'msgpack-lite';

export type {Platform};

export type Config = {
    urlRoot: string;
    platform: Platform;
    resolution: string;
    audioFormats: string[];
    audioQuality: string;
    locale: string;
    override: string | null;
    useWebPWASMDecoder: boolean;
    imageFormat: ImageFormat;
};

type ResourceBuilderConfig = {
    urlRoot: string;
    platform: Platform;
    resolution: string;
    audioFormats: AudioFormat[];
    audioQuality: AudioQuality;
    locale: string;
    override: string | null;
    useWebPWASMDecoder: boolean;
    imageFormat: ImageFormat;
};

export type LoaderMeta = {
    name: string;
    priority: number;
    optional?: boolean;
    audio?: MetaPackageInfo[];
    graphics?: MetaPackageInfo[];
    status: {
        graphics: LoaderStatus;
        audio: LoaderStatus;
    };
};

type LoaderStatus = {
    loaded: boolean;
    loaders: [loader: FileLoader<any>, size: number][];
    loadedPromise: Promise<void>;
    loadedPromiseResolver: () => void;
    variant?: MetaPackageInfo;
};

export type ToLoadPackageType = 'graphics' | 'audio' | 'all';
export type ProgressCallBack = (progress: number) => void;
export type LoadCallBack = (resources: Resources) => void;

const MAP_AUDIO_MIME_TYPE = {
    [AudioFormat.ogg]: 'application/ogg',
    [AudioFormat.m4a]: 'audio/m4a'
};

export class ResourceBuilderHandler {
    private meta: LoaderMeta[] = [];
    private config?: ResourceBuilderConfig;
    private initPromise?: Promise<void>;

    /**
     * frame time in ms
     */
    private lastTickDelta = 22;
    private lastTick = 0;

    private readonly resources: Resources = {
        sounds: {},
        spines: {},
        textures: {}
    };

    public updateConfig(config: Partial<Config>): this {
        this.config ??= ({} as any);

        const currentConfig = this.config!;
        const audioQuality = AudioQuality[config.audioQuality as keyof typeof AudioQuality] as AudioQuality | undefined;

        currentConfig.urlRoot = config.urlRoot ?? currentConfig.urlRoot;
        currentConfig.platform = config.platform ?? currentConfig.platform;
        currentConfig.useWebPWASMDecoder = currentConfig.useWebPWASMDecoder ?? config.useWebPWASMDecoder;
        currentConfig.imageFormat = currentConfig.imageFormat ?? config.imageFormat;
        currentConfig.locale = config.locale === undefined ? currentConfig.locale : config.locale;
        currentConfig.override = config.override === undefined ? currentConfig.override : config.override;
        currentConfig.resolution = config.resolution ?? currentConfig.resolution;
        currentConfig.audioFormats = (config.audioFormats as AudioFormat[]) ?? currentConfig.audioFormats;
        currentConfig.audioQuality = audioQuality ?? currentConfig.audioQuality;

        return this.updatePackagesToLoad();
    }

    public init(config: Config): Promise<void> {
        errorReporter.setTags({
            'fw:imageFormat': config.imageFormat,
            'fw:useWebPWASMDecoder': config.useWebPWASMDecoder
        });

        this.updateConfig(config);

        const binaryMetaUrl = this.config!.urlRoot + 'resources_meta.json';

        return this.initPromise = new FileLoader({
            url: binaryMetaUrl,
            responseType: 'json'
        })
            .load()
            .then((metaFile: ResourcesMetaFile) => this.parseMeta(metaFile));
    }

    public async load(options: {
        names?: string[];
        onProgress?: ProgressCallBack;
        onLoaded?: LoadCallBack;
        toLoad: ToLoadPackageType;
    }): Promise<void> {
        if (!this.initPromise) {
            throw new Error('Init first');
        }

        await this.initPromise;

        const {meta: meta} = this;
        const names = options.names || meta.map(item => item.name);
        const notLoadedPackages = this.getNotLoadedPackage(names);

        const toLoadPackages = notLoadedPackages
            .map(item => {
                const result = [];
                const loadAudio = options.toLoad === 'all' || options.toLoad === 'audio';
                const loadMain = options.toLoad === 'all' || options.toLoad === 'graphics';

                if (item.status.audio.variant && !item.status.audio.loaded && loadAudio) {
                    result.push(item.status.audio);
                }

                if (item.status.graphics.variant && !item.status.graphics.loaded && loadMain) {
                    result.push(item.status.graphics);
                }

                return result;
            })
            .flat();

        const onProgressUpdater = (): void => {
            let totalSize = 0;
            let loaded = 0;

            toLoadPackages.forEach(item => {
                item.loaders.forEach(([loader, size]) => {
                    totalSize += size;
                    loaded += loader.getLoaded();
                });
            });

            let progress = loaded / totalSize;

            // prevent error "0/0"!
            if (totalSize === 0) {
                progress = 0;
            }

            options.onProgress?.(progress);
        };

        const entries = toLoadPackages
            .map(status => status.variant!.files.map(([name, size]) => {
                return [status, this.config!.urlRoot + name, size] as [LoaderStatus, string, number];
            }))
            .flat();

        const promises = entries.map(([status, url, size]) => {
            const loader = new FileLoader({
                url,
                responseType: 'arraybuffer',
                progress: () => onProgressUpdater()
            });

            status.loaders.push([loader, size]);

            return loader.load();
        });

        const data = await Promise.all(promises);

        await this.extractResources(data);

        toLoadPackages.forEach(item => item.loaded = true);
        toLoadPackages.forEach(item => item.loadedPromiseResolver());

        options.onLoaded?.(this.resources);

        this.updateLoadedPackagesList();
    }

    private updateLoadedPackagesList() {
        const graphics = this.meta
            .filter(item => item.status.graphics.loaded)
            .map(item => item.name);

        const audio = this.meta
            .filter(item => item.status.audio.loaded)
            .map(item => item.name);

        coreStore.resources_loadedPackages.value = {
            audio,
            graphics,
        };
    }

    public async waitForPackage(name: string, type: ToLoadPackageType): Promise<LoaderMeta> {
        await this.initPromise;

        const packageData = this.getMetaPackageByName(name)!;
        const {status} = packageData;

        if (type === 'all') {
            await Promise.all([status.graphics.loadedPromise, status.audio.loadedPromise]);
        }

        if (type === 'graphics') {
            await status.graphics.loadedPromise;
        }

        if (type === 'audio') {
            await status.audio.loadedPromise;
        }

        return packageData;
    }

    public updatePackagesToLoad(): this {
        this.meta.forEach(packageMeta => this.findVariants(packageMeta));

        return this;
    }

    private getNotLoadedPackage(names: string[]): LoaderMeta[] {
        return this.meta.filter(item => {
            const mainNotLoaded = item.status.graphics.variant && !item.status.graphics.loaded;
            const audioNotLoaded = item.status.audio.variant && !item.status.audio.loaded;

            return names.includes(item.name) && (mainNotLoaded || audioNotLoaded);
        });
    }

    private async extractResources(data: ArrayBuffer[]): Promise<void> {
        const resources: BinaryDataPackage = {
            img: [],
            audio: [],
            spine: [],
            bitmapFont: []
        };

        data.forEach(item => {
            const data = msgpack.decode(new Uint8Array(item));

            resources.img.push(...(data.img || []));
            resources.audio.push(...(data.audio || []));
            resources.spine.push(...(data.spine || []));
            resources.bitmapFont.push(...(data.bitmapFont || []));
        });

        const {sounds, spines, textures} = this.resources;
        const promises = [];

        for (const img of resources.img) {
            if (textures[img.name]) {
                consoleLogger.warn(`ResourceBuilderHandler: Texture '${img.name}' already exists`);
            }

            textures[img.name] = this.decodeImage(img);

            if (textures[img.name].baseTexture.valid) {
                promises.push(Promise.resolve());
            } else {
                promises.push(new Promise(res => textures[img.name].baseTexture.once('loaded', res)));
            }

            if (this.nextDrawFramePause()) {
                await this.nextFrame();
            }
        }

        await Promise.all(promises);

        Object.values(textures).forEach(i => i.baseTexture.update());

        const loader = new Loader();
        const audio = resources.audio;

        audio.forEach(data => {
            const names = data.names.map(name => name.toLowerCase());
            const base64data = base64.encode(data.data, 0, data.data.length);
            const mimeType = MAP_AUDIO_MIME_TYPE[data.ext];
            const url = `data:${mimeType};base64,` + base64data;

            names.forEach(name => {
                loader.add(name, url, (data: any) => {
                    sounds[name] = data.sound as Sound;
                });
            });
        });

        await new Promise(res => loader.load(_ => res(undefined)));

        resources.spine.forEach(data => {
            const atlas = new TextureAtlas();
            const res = {
                data: data.data,
                metadata: {spineAtlas: atlas},
                type: LoaderResource.TYPE.JSON,
                xhrType: LoaderResource.XHR_RESPONSE_TYPE.JSON
            };

            atlas.addTextureHash({...textures}, true);

            // @ts-ignore
            SpineParser.use(res, () => {});
            spines[data.name] = (res as any).spineData as ISkeletonData;
        });

        resources.bitmapFont.forEach(binaryFontData => {
            const {data, name} = binaryFontData;
            const format = autoDetectFormat(data);

            if (!format) {
                throw new Error('Unrecognized data format for font.');
            }

            const fontData = format.parse(data as any);
            const images = fontData.page
                .map(item => {
                    const dotIndex = item.file.lastIndexOf('.');
                    const name = item.file.slice(0, dotIndex);

                    return textures[name];
                });

            // set font name in pixi to file name in resources
            fontData.info.forEach(info => info.face = name);

            BitmapFont.install(fontData, images);
        });
    }

    private decodeImage(img: BinaryDataImage): Texture {
        const {imageFormat, useWebPWASMDecoder} = this.config!;

        if (useWebPWASMDecoder) {
            return decodeImageWebP(img.data, img.resolution, img.width, img.height);
        } else {
            return decodeImage(img.data, `image/${imageFormat}`, img.resolution);
        }
    }

    private findVariants(packageMeta: LoaderMeta): void {
        const {platform, resolution, locale, audioFormats, audioQuality, imageFormat} = this.config!;
        const platformCode = PLATFORM_TYPE_MAP.find(item => item[0] === platform)![1];
        const override = this.config!.override || '*';
        const metaConfigSearch = [
            `${platformCode}:${locale}:${override}`,
            `${platformCode}:${locale}:*`,
            `${platformCode}:en:*`,
            `${platformCode}:*:*`
        ];

        function findConfig(configs: MetaPackageInfo[]): MetaPackageInfo | undefined {
            for (const pattern of metaConfigSearch) {
                for (const configItem of configs) {
                    if (configItem.configs.includes(pattern)) {
                        return configItem;
                    }
                }
            }
        }

        {
            let graphicsByResolution = (packageMeta.graphics || []).filter(item => {
                return item.imageResolution === resolution && item.imageFormat === imageFormat;
            });

            if (!graphicsByResolution.length) {
                graphicsByResolution = packageMeta.graphics || [];
            }

            packageMeta.status.graphics.variant = findConfig(graphicsByResolution);
        }

        {
            let formatVariants = (packageMeta.audio || []).filter(item => audioFormats.includes(item.audioFormat!));

            if (!formatVariants.length) {
                formatVariants = packageMeta.audio || [];
            }

            let qualityVariants = formatVariants.filter(item => item.audioQuality === audioQuality);

            if (!qualityVariants.length) {
                qualityVariants = formatVariants;
            }

            packageMeta.status.audio.variant = findConfig(qualityVariants);
        }
    }

    private createLoaderMeta(name: string, priority: number, optional: boolean): LoaderMeta {
        let loadedPromiseResolverMain: () => void;
        const loadedPromiseMain: Promise<void> = new Promise( res => loadedPromiseResolverMain = res);
        let loadedPromiseResolverAudio: () => void;
        const loadedPromiseAudio: Promise<void> = new Promise( res => loadedPromiseResolverAudio = res);

        return {
            name,
            priority,
            optional,
            status: {
                graphics: {
                    loaded: false,
                    loaders: [],
                    loadedPromise: loadedPromiseMain,
                    loadedPromiseResolver: loadedPromiseResolverMain!
                },
                audio: {
                    loaded: false,
                    loaders: [],
                    loadedPromise: loadedPromiseAudio,
                    loadedPromiseResolver: loadedPromiseResolverAudio!
                }
            }
        };
    }

    private parseMeta(metaFile: ResourcesMetaFile): void {
        for (const [type, collections] of Object.entries(metaFile)) {
            for (const [packageName, variantsGroup] of Object.entries(collections)) {
                let packageData = this.getMetaPackageByName(packageName);

                if (!packageData) {
                    this.meta.push(this.createLoaderMeta(packageName, variantsGroup.priority, !!variantsGroup.optional));
                    packageData = this.getMetaPackageByName(packageName);
                }

                packageData![type as keyof ResourcesMetaFile] = variantsGroup.variants;
            }
        }

        this.updatePackagesToLoad();
    }

    private getMetaPackageByName(name: string): LoaderMeta | undefined {
        return this.meta.find(item => item.name === name);
    }

    private nextFrame(): Promise<void> {
        return Promise.resolve();
    }

    private nextDrawFramePause(): boolean {
        const time = Date.now();

        if (time > this.lastTick + this.lastTickDelta) {
            this.lastTick = time;

            return true;
        }

        return false;
    }

}
